import { useCallback, useRef, useState } from "react";
import { Loader2, Upload } from "lucide-react";

import { finalizeCase, uploadCaseInstance } from "@/api/tests-api";

interface TestCaseUploaderProps {
  caseId: string;
  /** Called once the upload finishes, with what it actually stored. */
  onDone: (summary: Progress) => void;
}

/** How many uploads run at once.
 *
 *  A CORRECTNESS setting, not a speed knob. The backend runs a single
 *  gunicorn worker (worklist_hub is in-process), and each request decodes
 *  a DICOM instance. Push harder and the live clinic worklist queues
 *  behind a case library upload. Three keeps the pipe full without
 *  starving anyone. */
const CONCURRENCY = 3;

type Phase = "idle" | "uploading" | "done";

export interface Progress {
  total: number;
  processed: number;
  stored: number;
  reports: number;
  duplicates: number;
  failed: number;
  burnedIn: number;
  /** WHY the first file was skipped. The server returns a reason per
   *  file and this used to drop it, so "813 failed" was indistinguishable
   *  from 813 corrupt files, 813 unsupported image types and a case that
   *  had simply filled up. */
  skipReason: string | null;
}

const ZERO: Progress = {
  total: 0,
  processed: 0,
  stored: 0,
  reports: 0,
  duplicates: 0,
  failed: 0,
  burnedIn: 0,
  skipReason: null,
};

/**
 * Upload the DICOM files for one case.
 *
 * One file per request, walked by the browser, so there is a real
 * progress bar rather than one long silent wait — and so a study cannot
 * exceed the request body cap.
 *
 * A skipped file is not an error. A real export is mostly structured
 * reports and the occasional undecodable instance; the counters below
 * separate "ignored a report" from "could not decode an image", because
 * the first is normal and the second is worth looking at.
 */
export function TestCaseUploader({ caseId, onDone }: TestCaseUploaderProps) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [progress, setProgress] = useState<Progress>(ZERO);
  const [dragging, setDragging] = useState(false);
  // Why the upload stopped, when it stopped for a reason that applies to
  // every file. A per-file failure count alone said "15 failed" and left
  // an admin with nothing to act on.
  const [fatal, setFatal] = useState<string | null>(null);
  // The running tallies, readable synchronously. `progress` state drives
  // the bar and cannot be read back inside the loop that updates it —
  // a closure sees the value from its own render, not the latest.
  const tally = useRef<Progress>({ ...ZERO });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const folderRef = useRef<HTMLInputElement | null>(null);

  const run = useCallback(
    async (files: File[]) => {
      if (!files.length || phase === "uploading") return;
      setPhase("uploading");
      setFatal(null);
      tally.current = { ...ZERO, total: files.length };
      setProgress(tally.current);

      // Set once by any worker; the others check it and stop rather than
      // grinding through 300 files that will all fail the same way.
      let stop: string | null = null;

      let next = 0;
      const worker = async () => {
        for (;;) {
          if (stop) return;
          const index = next++;
          const file = files[index];
          if (!file) return;
          try {
            const res = await uploadCaseInstance(caseId, file);
            tally.current = {
              ...tally.current,
              processed: tally.current.processed + 1,
              stored: tally.current.stored + res.stored,
              reports: tally.current.reports + (res.not_an_image ? 1 : 0),
              duplicates: tally.current.duplicates + (res.duplicate ? 1 : 0),
              failed:
                tally.current.failed +
                (res.skipped && !res.not_an_image && !res.duplicate ? 1 : 0),
              burnedIn:
                tally.current.burnedIn + (res.burned_in_annotation ? 1 : 0),
              // The FIRST reason, not the last: they are overwhelmingly
              // all the same reason, and the first one arrived while the
              // admin was still watching.
              skipReason:
                tally.current.skipReason ??
                (res.skipped && !res.not_an_image && !res.duplicate
                  ? res.reason
                  : null),
            };
            setProgress(tally.current);
            // THE CASE IS FULL, so this is not "that file was bad" --
            // every remaining file is refused for the same reason. Same
            // argument as the 503 below, and the same treatment: stop,
            // and say what happened, rather than counting to 813.
            if (res.at_capacity) {
              stop =
                res.reason ??
                "This case is full. Raise the per-case image limit to add more.";
              setFatal(stop);
              return;
            }
          } catch (err) {
            const res = (err as { response?: { status?: number; data?: { detail?: string } } })
              ?.response;
            // 503 means the SERVER cannot decode anything -- carrying on
            // would produce hundreds of identical failures. Stop and say
            // so. Anything else is this one file's problem.
            if (res?.status === 503) {
              stop = res.data?.detail ?? "This server cannot decode images.";
              setFatal(stop);
              return;
            }
            tally.current = {
              ...tally.current,
              processed: tally.current.processed + 1,
              failed: tally.current.failed + 1,
              // A 413 is the commonest one here and says nothing on its
              // own; the server's detail names the cap.
              skipReason:
                tally.current.skipReason ??
                res?.data?.detail ??
                (res?.status
                  ? `the server answered ${res.status}`
                  : "the upload could not be sent"),
            };
            setProgress(tally.current);
          }
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, files.length) }, worker),
      );
      if (stop) {
        // Leave the case as it is: nothing was stored, and finalising
        // would mark an empty case READY.
        setPhase("idle");
        return;
      }
      await finalizeCase(caseId);
      setPhase("done");
      // Called DIRECTLY, not from inside a setState updater. Doing the
      // latter meant the refetch and the toast ran during another
      // component's render, where React defers or drops them — the list
      // then sat at "0 images" until the query went stale 30 seconds
      // later and something else refetched it.
      onDone(tally.current);
    },
    [caseId, phase, onDone],
  );

  /**
   * Files a PACS export folder contains that are not instances.
   *
   * DICOMDIR is an index, not an image, and every export has one. macOS
   * and Windows both scatter their own metadata through a copied folder.
   * Sending them would work — the backend skips what it cannot decode —
   * but they would land in the "could not be read" counter and make a
   * clean upload look like it had failures.
   */
  const isNoise = (file: File) => {
    const name = file.name.toUpperCase();
    return (
      name === "DICOMDIR" ||
      name === "THUMBS.DB" ||
      name === ".DS_STORE" ||
      name.startsWith("._")
    );
  };

  const pick = (list: FileList | File[] | null) => {
    if (!list) return;
    // No extension filter beyond the noise above: DICOM files routinely
    // have no extension at all.
    void run([...list].filter((f) => !isNoise(f)));
  };

  /**
   * Read a dropped DIRECTORY.
   *
   * `dataTransfer.files` is EMPTY when a folder is dropped — a directory
   * is not a file — which is why dropping a study folder silently did
   * nothing. The entries API is the only way to walk it, and it must be
   * read synchronously from the drop event before the DataTransfer is
   * neutered, hence collecting the entries first and awaiting after.
   */
  const readEntry = async (entry: FileSystemEntry): Promise<File[]> => {
    if (entry.isFile) {
      return new Promise<File[]>((resolve) => {
        (entry as FileSystemFileEntry).file(
          (f) => resolve([f]),
          () => resolve([]),
        );
      });
    }
    if (!entry.isDirectory) return [];

    const reader = (entry as FileSystemDirectoryEntry).createReader();
    const children: FileSystemEntry[] = [];
    // readEntries returns at most ~100 per call; keep going until empty.
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>((resolve) => {
        reader.readEntries(
          (es) => resolve(es),
          () => resolve([]),
        );
      });
      if (!batch.length) break;
      children.push(...batch);
    }
    const nested = await Promise.all(children.map(readEntry));
    return nested.flat();
  };

  const dropped = async (e: React.DragEvent) => {
    const items = [...e.dataTransfer.items];
    const entries = items
      .map((i) => (i.webkitGetAsEntry ? i.webkitGetAsEntry() : null))
      .filter((x): x is FileSystemEntry => !!x);

    if (!entries.length) {
      pick(e.dataTransfer.files);
      return;
    }
    const files = (await Promise.all(entries.map(readEntry))).flat();
    pick(files);
  };

  const pct = progress.total
    ? Math.round((progress.processed / progress.total) * 100)
    : 0;

  return (
    <div data-cy="case-uploader" className="mt-4">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void dropped(e);
        }}
        className={[
          "flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center transition-colors",
          dragging
            ? "border-accent bg-accent-light"
            : "border-border bg-background",
        ].join(" ")}
      >
        {phase === "uploading" ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin text-accent" aria-hidden />
            <p className="mt-2 text-sm text-foreground">
              {progress.processed} of {progress.total} files
            </p>
            <div
              className="mt-2 h-1.5 w-64 overflow-hidden rounded-full bg-border"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-full bg-accent transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </>
        ) : (
          <>
            <Upload className="h-5 w-5 text-foreground-muted" aria-hidden />
            <p className="mt-2 text-sm text-foreground">
              Drop the study's DICOM files here
            </p>
            <button
              type="button"
              data-cy="btn-choose-files"
              onClick={() => inputRef.current?.click()}
              className="mt-2 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:border-accent hover:text-accent"
            >
              Choose files
            </button>
            <button
              type="button"
              data-cy="btn-choose-folder"
              onClick={() => folderRef.current?.click()}
              className="ml-2 mt-2 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:border-accent hover:text-accent"
            >
              Choose folder
            </button>
            <input
              ref={inputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => pick(e.target.files)}
            />
            {/* webkitdirectory is not in React's typings; it is supported
                in every browser this runs on. */}
            <input
              ref={folderRef}
              type="file"
              multiple
              hidden
              {...({
                webkitdirectory: "",
                directory: "",
              } as Record<string, string>)}
              onChange={(e) => pick(e.target.files)}
            />
            <p className="mt-2 text-[11px] text-foreground-muted">
              Drop a study folder, or choose one. Sub-folders are read too.
              Reports and other non-image files are ignored.
            </p>
            {fatal && (
              <p
                data-cy="upload-fatal"
                className="mt-3 max-w-md rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
              >
                Upload stopped — nothing was saved. {fatal}
              </p>
            )}
          </>
        )}
      </div>

      {progress.processed > 0 && (
        <dl
          data-cy="upload-summary"
          className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs"
        >
          <Stat label="Images stored" value={progress.stored} tone="ok" />
          {progress.reports > 0 && (
            <Stat label="Reports ignored" value={progress.reports} />
          )}
          {progress.duplicates > 0 && (
            <Stat label="Already uploaded" value={progress.duplicates} />
          )}
          {progress.failed > 0 && (
            <Stat
              label="Could not be read"
              value={progress.failed}
              tone="warn"
            />
          )}
        </dl>
      )}

      {/* WHAT WENT WRONG, not just how many times. The count alone made
          a case that had simply filled up look identical to a folder of
          corrupt files. */}
      {progress.failed > 0 && progress.skipReason && (
        <p
          data-cy="upload-skip-reason"
          className="mt-2 text-xs text-status-wip"
        >
          First of the {progress.failed}: {progress.skipReason}
        </p>
      )}

      {progress.burnedIn > 0 && (
        <p
          data-cy="burned-in-warning"
          className="mt-2 rounded-md bg-status-wip-bg px-3 py-2 text-xs text-status-wip"
        >
          {progress.burnedIn} image
          {progress.burnedIn === 1 ? "" : "s"} report burned-in annotation.
          Check the pictures themselves for a patient name — stripping the
          DICOM header does not remove text drawn into the pixels.
        </p>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "ok" | "warn";
}) {
  const colour =
    tone === "ok"
      ? "text-status-done"
      : tone === "warn"
        ? "text-status-urgent"
        : "text-foreground-muted";
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-foreground-muted">{label}</dt>
      <dd className={`font-semibold tabular-nums ${colour}`}>{value}</dd>
    </div>
  );
}

/**
 * One sentence for what an upload did.
 *
 * Shared so the two places that report it — creating a case, and adding
 * images to an existing one — cannot word the same outcome differently.
 * A skip is not a failure: a real export is mostly structured reports,
 * so they are counted separately or the ordinary case reads as trouble.
 */
export function summarise(p: Progress): string {
  if (p.stored === 0) {
    return p.reports > 0
      ? `No images stored — all ${p.reports} files were reports or non-images.`
      : `No images could be read from those files.${
          p.skipReason ? ` ${p.skipReason}.` : ""
        }`;
  }
  const extra: string[] = [];
  if (p.reports) extra.push(`${p.reports} report${p.reports === 1 ? "" : "s"} ignored`);
  if (p.duplicates) extra.push(`${p.duplicates} already present`);
  if (p.failed) {
    extra.push(
      `${p.failed} could not be read${p.skipReason ? `: ${p.skipReason}` : ""}`,
    );
  }
  const tail = extra.length ? ` (${extra.join(", ")})` : "";
  return `${p.stored} image${p.stored === 1 ? "" : "s"} stored${tail}.`;
}
